import { useMemo } from 'react';
import { isSupportedStateCode } from '@/common/constants/states';
import { useInteraction } from '@/common/hooks/useInteraction';
import type { MissingFieldsData } from '@/state/navigation';
import { useAppFlags } from '$launch-darkly/app-flags';
import { computeMissingLeaseContactFields } from './lease/missingFields';
import { computeMissingListingContactFields } from './listing/missingFields';
import {
  buildOfferContactsConfig,
  resolveOfferContactsConfig,
} from './offer/formConfig';
import { computeMissingOfferContactFields } from './offer/missingFields';
import { hydrateContacts } from './shared/hydrate';
import { useContactsData } from './shared/useContactsData';

const SUPPORTED_TYPES: string[] = ['offer', 'listing', 'lease'];

/**
 * The contacts page's missing required fields, for the sidebar badge + documents
 * lockdown. Returns [] unless the flag is on for a supported interaction type.
 */
export function useContactMissingFields(
  interactionId: string,
  interactionType: string,
): MissingFieldsData[] {
  const { txmCoverSheetContactsOffConfig } = useAppFlags();
  const enabled =
    !!txmCoverSheetContactsOffConfig &&
    SUPPORTED_TYPES.includes(interactionType);
  const isLease = interactionType === 'lease';

  const { data: interaction } = useInteraction(
    interactionId,
    interactionType,
    !enabled,
  );
  const rawState = interaction?.property?.state;
  const representation = interaction?.representation;

  const { data: contacts } = useContactsData(interactionId, !enabled);

  return useMemo(() => {
    if (!enabled || !contacts) return [];
    const formData = hydrateContacts(contacts);

    if (interactionType === 'listing') {
      return computeMissingListingContactFields(formData);
    }
    if (isLease) {
      return computeMissingLeaseContactFields(
        formData,
        representation ?? 'dual',
      );
    }
    const config = isSupportedStateCode(rawState)
      ? resolveOfferContactsConfig(rawState)
      : buildOfferContactsConfig();
    return computeMissingOfferContactFields(formData, config);
  }, [enabled, contacts, rawState, interactionType, isLease, representation]);
}
